import React, { useState, useMemo } from "react";
import { api } from "@/lib/api";
import { X, ClipboardCheck, Activity, Brain, Heart, Moon, Utensils, Zap } from "lucide-react";

const COMPONENT_TEMPLATE = [
  { label: "Physical Health",   icon: Activity, color: "#10B981" },
  { label: "Mental Health",     icon: Brain,    color: "#A855F7" },
  { label: "Emotional Balance", icon: Heart,    color: "#F43F5E" },
  { label: "Sleep Quality",     icon: Moon,     color: "#6366F1" },
  { label: "Nutrition",         icon: Utensils, color: "#F97316" },
  { label: "Energy Levels",     icon: Zap,      color: "#EAB308" },
];

// Dropdown options — 5-point increments + descriptive labels
const SCORE_OPTIONS = [
  { value: 0,   label: "0 — N/A" },
  { value: 10,  label: "10 — Very Poor" },
  { value: 20,  label: "20 — Poor" },
  { value: 30,  label: "30 — Below Average" },
  { value: 40,  label: "40 — Fair" },
  { value: 50,  label: "50 — Average" },
  { value: 60,  label: "60 — Above Average" },
  { value: 70,  label: "70 — Good" },
  { value: 80,  label: "80 — Very Good" },
  { value: 90,  label: "90 — Excellent" },
  { value: 100, label: "100 — Optimal" },
];

const nearestStep = (n) => {
  const step = Math.round((n || 0) / 10) * 10;
  return Math.max(0, Math.min(100, step));
};

/**
 * WellnessAssessmentModal — provider-facing form to record a patient's wellness
 * across the 6 components during a meeting. Saves to `/api/patients/{id}/wellness-assessment`.
 * Props:
 *   patientId — string
 *   initialComponents — [{label, score}] used to pre-populate the dropdowns
 *   onClose, onSaved — callbacks
 */
export default function WellnessAssessmentModal({ patientId, initialComponents = [], onClose, onSaved }) {
  const initialMap = useMemo(
    () => Object.fromEntries((initialComponents || []).map((c) => [c.label, c.score])),
    [initialComponents]
  );
  const [scores, setScores] = useState(() =>
    Object.fromEntries(COMPONENT_TEMPLATE.map((c) => [c.label, nearestStep(initialMap[c.label] ?? 70)]))
  );
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const overall = useMemo(() => {
    const vals = Object.values(scores);
    return vals.length ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : 0;
  }, [scores]);

  const handleSave = async () => {
    setBusy(true);
    setErr(null);
    try {
      const components = COMPONENT_TEMPLATE.map((c) => ({ label: c.label, score: scores[c.label] }));
      const r = await api.post(`/patients/${patientId}/wellness-assessment`, { components, note: note || null });
      onSaved?.(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save assessment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="wellness-assessment-modal">
      <div className="bg-white rounded-2xl shadow-elevated w-full max-w-2xl border border-stone-200 overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-5 py-4 border-b border-stone-100 flex justify-between items-center bg-gradient-to-r from-emerald-50/60 to-white">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-emerald-600 text-white grid place-items-center shadow-sm">
              <ClipboardCheck className="w-4.5 h-4.5" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-stone-900">Record Wellness Assessment</h3>
              <p className="text-xs text-stone-500">Enter the patient's scores from today's meeting. Overall score recalculates live.</p>
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-stone-400" /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-3" data-testid="wellness-rows">
          {COMPONENT_TEMPLATE.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="grid grid-cols-12 gap-3 items-center" data-testid={`wellness-row-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="col-span-5 flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-lg grid place-items-center" style={{ backgroundColor: `${c.color}15`, color: c.color }}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="text-sm font-semibold text-stone-900">{c.label}</span>
                </div>
                <div className="col-span-5">
                  <select
                    value={scores[c.label]}
                    onChange={(e) => setScores((s) => ({ ...s, [c.label]: parseInt(e.target.value, 10) }))}
                    className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 focus:outline-none"
                    data-testid={`wellness-score-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {SCORE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${scores[c.label]}%`, backgroundColor: c.color }} />
                  </div>
                  <div className="text-[10px] text-stone-500 text-right mt-1">{scores[c.label]}%</div>
                </div>
              </div>
            );
          })}

          <div className="pt-2">
            <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-1 block">Meeting note (optional)</label>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Patient reported good appetite this week; some fatigue lingering."
              className="w-full px-3 py-2 rounded-lg border border-stone-300 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 focus:outline-none"
              data-testid="wellness-note"
            />
          </div>

          {err && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700" data-testid="wellness-error">
              {err}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-stone-100 flex items-center justify-between bg-stone-50/40">
          <div className="text-sm text-stone-700">
            <span className="text-stone-500">Current Overall Score:</span>{" "}
            <span className="text-lg font-bold text-emerald-700" data-testid="wellness-overall-live">{overall}</span>
            <span className="text-stone-500">/100</span>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-lg border border-stone-300 text-sm font-medium hover:bg-stone-50 disabled:opacity-60">Cancel</button>
            <button onClick={handleSave} disabled={busy} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-60" data-testid="wellness-save-btn">
              {busy ? "Saving…" : "Save assessment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
